/**
 * Return Portal content editor: static refund method display name and description (portal-edge scope).
 * Shown only when ResolutionStaticTranslation capability is enabled for the merchant.
 * Save/discard/hasChanges use refundMethodsContentStore (correct TranslationScopeStore), not the
 * parent assistant TranslationScope from useTranslation.
 */

import { observer } from 'mobx-react';
import { useCallback, useEffect, useState, type FC } from 'react';
import { Navigate } from 'react-router-dom';
import usePage from '../../../../../../components/layouts/base-page-layout/page-hook';
import useTranslation from '../../../../../../translation/hooks/translation-hook';
import useNavigationBlocker from '../../../../../../hooks/navigation-blocker-hook';
import TranslationScope from '../../../../../../translation/translation-scope';
import EditorPageLayout from '../../../../../../components/layouts/editor-page/editor-page-layout';
import { ConfirmModal } from '../../../../../../components/modal/components/confirm-modal/confirm-modal';
import ListLayout from '../../../../../../components/layouts/list-layout/list-layout';
import { FormField } from '../../../../../../components/form-field/form-field';
import TranslationInput from '../../../../../../translation/translation-input/translation-input';
import TranslationTextArea from '../../../../../../translation/translation-textarea/translation-textarea';
import currentPortalStore from '../../../../../../stores/current-portal-store';
import dashboardSettingsStore from '../../../../../../stores/dashboard-settings-store';
import { PROFILE_CAPABILITIES } from '../../../../../../models/ProfileCapabilities';
import { RESOLUTION_TYPE } from '../../../../../../pages/resolutions/constants';
import { APP_PATHS } from '../../../../../../constants/app-paths';
import Card from '../../../../../../components/layouts/card/card';
import FontBody1 from '../../../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../../components/fonts/font-props';
import {
    getAllRefundMethodStaticTranslationKeys,
    getVisibleRefundMethodStaticBlocks,
} from '../../constants/refund-method-static-blocks';
import refundMethodsContentStore from '../../stores/refund-methods-content-store';

/** Registers refund static keys on the inner TranslationScope for missing-translation helpers. */
const RefundMethodsTranslatableKeysEffect: FC<{ supportedRefundTypes: RESOLUTION_TYPE[] }> = ({
    supportedRefundTypes,
}) => {
    const { setTranslatableKeys } = useTranslation();

    useEffect(() => {
        setTranslatableKeys(getAllRefundMethodStaticTranslationKeys(supportedRefundTypes));
        return () => setTranslatableKeys([]);
    }, [setTranslatableKeys, supportedRefundTypes]);

    return null;
};

const RefundMethodsEditorPage = observer(() => {
    const { setSubTitles } = usePage();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const resolutionStaticTranslation = dashboardSettingsStore.resolveCapability(
        PROFILE_CAPABILITIES.PORTAL_TRANSLATIONS
    );
    const isEnabled = resolutionStaticTranslation?.isRefundMethodsScopeSupported === true;

    const refundResolutionCapability = dashboardSettingsStore.resolveCapability(
        PROFILE_CAPABILITIES.REFUND_RESOLUTION
    );
    const visibleBlocks = getVisibleRefundMethodStaticBlocks(
        refundResolutionCapability.supportedRefundTypes
    );

    useEffect(() => {
        setSubTitles(['Portal', 'Content', 'Return Portal', 'Refund Methods']);
    }, [setSubTitles]);

    const { selectedLocale } = useTranslation();

    const selectedScope = refundMethodsContentStore.resolveScope(
        !!currentPortalStore.setting?.enablePortalV2
    );

    const hasChanges = () => refundMethodsContentStore.hasChanges(selectedScope);

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        refundMethodsContentStore.discardChanges(selectedScope);
    };

    const onSaveClicked = () => {
        const promised: Promise<unknown>[] = [];
        if (refundMethodsContentStore.hasChanges(selectedScope)) {
            promised.push(refundMethodsContentStore.save(selectedScope));
        }
        Promise.all(promised).then(() => {
            unblockNavigation();
        });
    };

    if (!isEnabled) {
        return <Navigate to={APP_PATHS.SETTINGS_PORTAL_CONTENT} replace />;
    }

    return (
        <TranslationScope scope={selectedScope} locale={selectedLocale}>
            <RefundMethodsTranslatableKeysEffect
                supportedRefundTypes={refundResolutionCapability.supportedRefundTypes}
            />
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        disabled: !hasChanges(),
                        onClick: onSaveClicked,
                    },
                }}
            >
                <ListLayout>
                    {visibleBlocks.map((block) => (
                        <Card
                            key={block.id}
                            toolbar={{
                                title: (
                                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                        {block.blockTitle}
                                    </FontBody1>
                                ),
                            }}
                        >
                            <ListLayout>
                                <FormField label="Display name">
                                    <TranslationInput translationKey={block.nameKey} />
                                </FormField>
                                <FormField label="Description">
                                    <TranslationTextArea
                                        translationKey={block.descriptionKey}
                                        maxLength={200}
                                        showCharactersCount
                                    />
                                </FormField>
                            </ListLayout>
                        </Card>
                    ))}
                </ListLayout>
            </EditorPageLayout>

            {isShowUnsaved && (
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false),
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        },
                    }}
                />
            )}
        </TranslationScope>
    );
});

export default RefundMethodsEditorPage;
